import React, { useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Tag,
  Alert,
  message,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  TeamOutlined,
  CalendarOutlined,
  CoffeeOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type {
  Reservation,
  RestaurantTable,
} from "../../utils/api/reservationsAPI";
import "./AddReservationModal.scss";

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: any) => Promise<void>;
  editData?: Reservation | null;
  loading?: boolean;
  tables?: RestaurantTable[];
  existingReservations?: Reservation[];
};

const AddReservationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  editData,
  loading = false,
  tables = [],
  existingReservations = [],
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEdit = !!editData;

  // Watch date + time + tableIds + numberOfGuests for live calculations
  const selectedDate = Form.useWatch("date", form);
  const selectedTime = Form.useWatch("time", form);
  const selectedTableIds: string[] = Form.useWatch("tableIds", form) || [];
  const watchedGuests: number = Form.useWatch("numberOfGuests", form) || 0;

  const activeTables = tables.filter((t) => t.isActive);

  // Collect all taken table IDs at the selected date+time (handles both old tableId and new tableIds)
  const takenTableIds = useMemo(() => {
    if (!selectedDate || !selectedTime) return new Set<string>();
    const dateStr = dayjs(selectedDate).format("YYYY-MM-DD");
    return new Set(
      existingReservations
        .filter((r) => {
          const rDate = dayjs(r.date).format("YYYY-MM-DD");
          const isCurrentEdit = editData && r._id === editData._id;
          return (
            rDate === dateStr &&
            r.time === selectedTime &&
            !["cancelled", "no-show"].includes(r.status) &&
            !isCurrentEdit
          );
        })
        .flatMap((r) => {
          if (r.tableIds?.length) return r.tableIds;
          if (r.tableId) return [r.tableId];
          return [];
        })
    );
  }, [selectedDate, selectedTime, existingReservations, editData]);

  // Capacity validation: sum of selected tables vs number of guests
  const capacityInfo = useMemo(() => {
    if (!selectedTableIds.length) return null;
    const selected = activeTables.filter((t) => selectedTableIds.includes(t._id));
    const totalCap = selected.reduce((sum, t) => sum + t.capacity, 0);
    return { totalCap, exceeded: watchedGuests > totalCap };
  }, [selectedTableIds, watchedGuests, activeTables]);

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        // Support both old (tableId) and new (tableIds) format
        const tableIds =
          editData.tableIds?.length
            ? editData.tableIds
            : editData.tableId
            ? [editData.tableId]
            : [];
        form.setFieldsValue({
          guestName: editData.guestName,
          guestPhone: editData.guestPhone,
          guestEmail: editData.guestEmail || "",
          date: dayjs(editData.date),
          time: editData.time,
          numberOfGuests: editData.numberOfGuests,
          note: editData.note || "",
          source: editData.source,
          status: editData.status,
          tableIds,
          waiterName: editData.waiterName || "",
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          date: dayjs(),
          source: "internal",
          status: "confirmed",
          numberOfGuests: 2,
          tableIds: [],
        });
      }
    }
  }, [isOpen, editData]);

  const onFinish = async (values: any) => {
    const selectedTables = activeTables.filter((t) =>
      (values.tableIds || []).includes(t._id)
    );
    try {
      await onSave({
        ...values,
        date: values.date ? values.date.format("YYYY-MM-DD") : undefined,
        tableIds: values.tableIds || [],
        tableNames: selectedTables.map((t) => t.name),
        // Backwards compat — first selected table
        tableId: values.tableIds?.[0] || null,
        tableName: selectedTables[0]?.name || null,
        waiterName: values.waiterName?.trim() || null,
      });
      form.resetFields();
    } catch (err) {
      message.error(t("Failed to save reservation"));
    }
  };

  return (
    <Modal
      title={
        <div className="arm-title">
          <CalendarOutlined />
          <span>{isEdit ? t("Edit Reservation") : t("New Reservation")}</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={520}
      className="add-reservation-modal"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="arm-form"
        requiredMark={false}
      >
        <Form.Item
          name="guestName"
          label={t("Guest Name")}
          rules={[{ required: true, message: t("Guest name is required") }]}
        >
          <Input prefix={<UserOutlined />} placeholder={t("Full name")} size="large" />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="guestPhone"
              label={t("Phone")}
              rules={[{ required: true, message: t("Phone is required") }]}
            >
              <Input prefix={<PhoneOutlined />} placeholder="+381 60 123 4567" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="guestEmail"
              label={t("Email (optional)")}
              rules={[{ type: "email", message: t("Invalid email") }]}
            >
              <Input prefix={<MailOutlined />} placeholder="email@example.com" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="date"
              label={t("Date")}
              rules={[{ required: true, message: t("Date is required") }]}
            >
              <DatePicker size="large" style={{ width: "100%" }} format="DD.MM.YYYY" allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="time"
              label={t("Time")}
              rules={[{ required: true, message: t("Time is required") }]}
            >
              <Select size="large" placeholder={t("Select time")} showSearch>
                {TIME_OPTIONS.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="numberOfGuests"
              label={t("Guests")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <InputNumber
                prefix={<TeamOutlined />}
                min={1} max={100} size="large" style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Multi-table selector — only shown if tables are configured */}
        {activeTables.length > 0 && (
          <>
            <Form.Item name="tableIds" label={t("Tables")}>
              <Select
                mode="multiple"
                size="large"
                placeholder={t("No tables assigned")}
                allowClear
                optionLabelProp="label"
              >
                {activeTables.map((table) => {
                  const isTaken = takenTableIds.has(table._id);
                  return (
                    <Select.Option
                      key={table._id}
                      value={table._id}
                      label={table.name}
                      disabled={isTaken}
                    >
                      <div className="arm-table-option">
                        <span>{table.name}</span>
                        <span className="arm-table-capacity">
                          {table.capacity} {t("seats")}
                        </span>
                        {isTaken && (
                          <Tag color="red" style={{ marginLeft: 6, fontSize: 11 }}>
                            {t("Taken")}
                          </Tag>
                        )}
                      </div>
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            {capacityInfo && capacityInfo.exceeded && (
              <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                message={t("Capacity exceeded")}
                description={`${watchedGuests} ${t("guests")} — ${t("selected tables fit")} ${capacityInfo.totalCap} ${t("seats")}`}
                style={{ marginTop: -16, marginBottom: 16 }}
              />
            )}

            {capacityInfo && !capacityInfo.exceeded && selectedTableIds.length > 0 && (
              <div className="arm-capacity-ok" style={{ marginTop: -12, marginBottom: 16, fontSize: 12, color: "#6b7280" }}>
                {t("Total capacity")}: {capacityInfo.totalCap} {t("seats")}
              </div>
            )}
          </>
        )}

        <Form.Item name="note" label={t("Special Requests / Note")}>
          <Input.TextArea
            rows={2}
            placeholder={t("Allergies, birthday, high chair needed...")}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item name="waiterName" label={t("Waiter")}>
          <Input
            prefix={<CoffeeOutlined />}
            placeholder={t("Waiter name (optional)")}
            size="large"
          />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="source" label={t("Source")}>
              <Select size="large">
                <Select.Option value="internal">{t("Internal")}</Select.Option>
                <Select.Option value="phone">{t("Phone")}</Select.Option>
                <Select.Option value="website">{t("Website")}</Select.Option>
                <Select.Option value="widget">{t("Widget")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="status" label={t("Status")}>
              <Select size="large">
                <Select.Option value="pending">{t("Pending")}</Select.Option>
                <Select.Option value="confirmed">{t("Confirmed")}</Select.Option>
                <Select.Option value="seated">{t("Seated")}</Select.Option>
                <Select.Option value="completed">{t("Completed")}</Select.Option>
                <Select.Option value="cancelled">{t("Cancelled")}</Select.Option>
                <Select.Option value="no-show">{t("No Show")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="arm-footer">
          <Button size="large" onClick={onClose}>{t("Cancel")}</Button>
          <Button type="primary" size="large" htmlType="submit" loading={loading}>
            {isEdit ? t("Save Changes") : t("Add Reservation")}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddReservationModal;
